/**
 * CustomerSelectorModal — guided modal for selecting or creating a customer.
 *
 * Step 1: Choose "Existing Customer" or "New Customer"
 * Step 2a: If existing → delegates to MetaFieldEditModal (existing behavior)
 * Step 2b: If new → inline form with:
 *   - Customer info: name, type (Individual/Company), phone, email, tax_id
 *   - Address: line1, city, state/province, postal code (defaults to Thailand)
 *   Creates Customer + Address (linked) via frappe.client.insert
 */

import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Loader2,
  UserPlus,
  Search,
  ArrowLeft,
  Building2,
  User,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getERPClient } from "@/lib/erp/client";

interface CustomerSelectorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the customer name (ERPNext name field) after selection or creation */
  onSelectCustomer: (customerName: string) => void;
}

type Step = "choose" | "create";
type CustomerType = "Individual" | "Company";

interface NewCustomerForm {
  customer_name: string;
  customer_type: CustomerType;
  mobile_no: string;
  email_id: string;
  tax_id: string;
  address_line1: string;
  city: string;
  state: string;
  pincode: string;
}

const INITIAL_FORM: NewCustomerForm = {
  customer_name: "",
  customer_type: "Individual",
  mobile_no: "",
  email_id: "",
  tax_id: "",
  address_line1: "",
  city: "",
  state: "",
  pincode: "",
};

export function CustomerSelectorModal({
  open,
  onOpenChange,
  onSelectCustomer,
}: CustomerSelectorModalProps) {
  const [step, setStep] = useState<Step>("choose");
  const [form, setForm] = useState<NewCustomerForm>(INITIAL_FORM);
  const queryClient = useQueryClient();

  const setField = useCallback(
    <K extends keyof NewCustomerForm>(key: K, value: NewCustomerForm[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const createMutation = useMutation({
    mutationFn: async (data: NewCustomerForm) => {
      const client = getERPClient();

      // 1. Create Customer
      const customer = (await client.call("frappe.client.insert", {
        doc: {
          doctype: "Customer",
          customer_name: data.customer_name,
          customer_type: data.customer_type,
          mobile_no: data.mobile_no || undefined,
          email_id: data.email_id || undefined,
          tax_id: data.tax_id || undefined,
        },
      })) as { name: string; customer_name: string };

      // 2. Create Address linked to customer (if address provided)
      const hasAddress = data.address_line1.trim();
      if (hasAddress) {
        await client.call("frappe.client.insert", {
          doc: {
            doctype: "Address",
            address_title: data.customer_name,
            address_type: "Billing",
            address_line1: data.address_line1,
            city: data.city || "",
            state: data.state || "",
            pincode: data.pincode || "",
            country: "Thailand",
            phone: data.mobile_no || "",
            email_id: data.email_id || "",
            is_primary_address: 1,
            is_shipping_address: 1,
            links: [
              {
                link_doctype: "Customer",
                link_name: customer.name,
              },
            ],
          },
        });
      }

      return customer;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["pos", "customers"] });
      onSelectCustomer(data.name);
      toast.success(`สร้างลูกค้า "${data.customer_name}" เรียบร้อย`);
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast.error(error?.message || "ไม่สามารถสร้างลูกค้าได้ กรุณาลองอีกครั้ง");
    },
  });

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setStep("choose");
      setForm(INITIAL_FORM);
    }
  }, [open]);

  const handleCreate = useCallback(() => {
    if (!form.customer_name.trim()) return;
    createMutation.mutate({
      ...form,
      customer_name: form.customer_name.trim(),
    });
  }, [form, createMutation]);

  const handleChooseExisting = useCallback(() => {
    onOpenChange(false);
    onSelectCustomer("");
  }, [onOpenChange, onSelectCustomer]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] p-0 overflow-hidden">
        {/* ── Step 1: Choose ── */}
        {step === "choose" && (
          <div className="px-6 py-6 space-y-5">
            <div className="space-y-1">
              <h2 className="text-lg font-semibold">เลือกลูกค้า</h2>
              <p className="text-sm text-muted-foreground">
                เลือกลูกค้าที่มีอยู่แล้ว หรือสร้างลูกค้าใหม่
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleChooseExisting}
                className={cn(
                  "flex flex-col items-center gap-3 rounded-2xl border-2 p-5 transition-all duration-150",
                  "hover:border-sky-400 hover:bg-sky-50/50 dark:hover:bg-sky-950/20",
                  "focus:outline-none focus:ring-2 focus:ring-sky-400/40",
                  "border-border bg-card",
                )}
              >
                <div className="rounded-full bg-sky-50 dark:bg-sky-950/30 p-3">
                  <Search className="h-6 w-6 text-sky-600" />
                </div>
                <div className="text-center space-y-0.5">
                  <p className="text-sm font-semibold">ลูกค้าที่มีอยู่</p>
                  <p className="text-xs text-muted-foreground">Existing Customer</p>
                </div>
              </button>

              <button
                onClick={() => setStep("create")}
                className={cn(
                  "flex flex-col items-center gap-3 rounded-2xl border-2 p-5 transition-all duration-150",
                  "hover:border-emerald-400 hover:bg-emerald-50/50 dark:hover:bg-emerald-950/20",
                  "focus:outline-none focus:ring-2 focus:ring-emerald-400/40",
                  "border-border bg-card",
                )}
              >
                <div className="rounded-full bg-emerald-50 dark:bg-emerald-950/30 p-3">
                  <UserPlus className="h-6 w-6 text-emerald-600" />
                </div>
                <div className="text-center space-y-0.5">
                  <p className="text-sm font-semibold">ลูกค้าใหม่</p>
                  <p className="text-xs text-muted-foreground">New Customer</p>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* ── Step 2: Create new customer ── */}
        {step === "create" && (
          <div className="space-y-0">
            {/* Header */}
            <div className="flex items-center gap-3 px-6 pt-5 pb-4">
              <button
                onClick={() => setStep("choose")}
                className="rounded-full p-1.5 hover:bg-muted transition-colors"
              >
                <ArrowLeft className="h-4 w-4 text-muted-foreground" />
              </button>
              <h2 className="text-base font-semibold">สร้างลูกค้าใหม่</h2>
            </div>

            <div className="space-y-4 px-6 pb-6 max-h-[65vh] overflow-y-auto">
              {/* Customer Type Toggle */}
              <div className="space-y-2">
                <label className="text-sm font-medium">ประเภทลูกค้า</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setField("customer_type", "Individual")}
                    className={cn(
                      "flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm transition-all duration-150",
                      form.customer_type === "Individual"
                        ? "border-sky-400 bg-sky-50 dark:bg-sky-950/30 font-medium text-sky-700 dark:text-sky-300"
                        : "border-border hover:border-muted-foreground/40 hover:bg-muted/50",
                    )}
                  >
                    <User className={cn("h-4 w-4", form.customer_type === "Individual" ? "text-sky-600" : "text-muted-foreground")} />
                    <span>บุคคล</span>
                  </button>
                  <button
                    onClick={() => setField("customer_type", "Company")}
                    className={cn(
                      "flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm transition-all duration-150",
                      form.customer_type === "Company"
                        ? "border-sky-400 bg-sky-50 dark:bg-sky-950/30 font-medium text-sky-700 dark:text-sky-300"
                        : "border-border hover:border-muted-foreground/40 hover:bg-muted/50",
                    )}
                  >
                    <Building2 className={cn("h-4 w-4", form.customer_type === "Company" ? "text-sky-600" : "text-muted-foreground")} />
                    <span>บริษัท</span>
                  </button>
                </div>
              </div>

              {/* Customer Name */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {form.customer_type === "Company" ? "ชื่อบริษัท" : "ชื่อลูกค้า"}
                </label>
                <Input
                  placeholder={form.customer_type === "Company" ? "บริษัท ABC จำกัด" : "ชื่อ-นามสกุล"}
                  value={form.customer_name}
                  onChange={(e) => setField("customer_name", e.target.value)}
                  autoFocus
                />
              </div>

              {/* Phone + Email row */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="text-sm font-medium">เบอร์โทร</label>
                  <Input
                    placeholder="08x-xxx-xxxx"
                    value={form.mobile_no}
                    onChange={(e) => setField("mobile_no", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">อีเมล</label>
                  <Input
                    type="email"
                    placeholder="email@example.com"
                    value={form.email_id}
                    onChange={(e) => setField("email_id", e.target.value)}
                  />
                </div>
              </div>

              {/* Tax ID */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  เลขประจำตัวผู้เสียภาษี
                  <span className="text-muted-foreground font-normal ml-1">(ไม่บังคับ)</span>
                </label>
                <Input
                  placeholder="เลข 13 หลัก"
                  value={form.tax_id}
                  onChange={(e) => setField("tax_id", e.target.value)}
                />
              </div>

              {/* ── Address Section ── */}
              <div className="pt-2">
                <p className="text-sm font-medium text-foreground/70 mb-3">ที่อยู่สำหรับจัดส่ง / ออกบิล</p>

                <div className="space-y-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">ที่อยู่</label>
                    <Input
                      placeholder="เลขที่ ซอย ถนน"
                      value={form.address_line1}
                      onChange={(e) => setField("address_line1", e.target.value)}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">เขต / อำเภอ</label>
                      <Input
                        placeholder="เขต/อำเภอ"
                        value={form.city}
                        onChange={(e) => setField("city", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">จังหวัด</label>
                      <Input
                        placeholder="จังหวัด"
                        value={form.state}
                        onChange={(e) => setField("state", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="w-1/2">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">รหัสไปรษณีย์</label>
                      <Input
                        placeholder="10xxx"
                        value={form.pincode}
                        onChange={(e) => setField("pincode", e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 rounded-xl"
                  onClick={() => onOpenChange(false)}
                  disabled={createMutation.isPending}
                >
                  ยกเลิก
                </Button>
                <Button
                  className="flex-1 rounded-xl"
                  onClick={handleCreate}
                  disabled={createMutation.isPending || !form.customer_name.trim()}
                >
                  {createMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      กำลังสร้าง...
                    </>
                  ) : (
                    "สร้างลูกค้า"
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
